/**
 * TransactionRegisterPage (등록 화면)
 *
 * 새로운 수입/지출 거래를 등록하는 페이지
 * - 수입/지출 탭 전환
 * - TransactionForm 통합
 * - 에러 토스트 표시
 * - Sticky 헤더 + 탭 레이아웃
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { TransactionType, TransactionInput } from '../types/Transaction'
import { TransactionForm } from '../components/TransactionForm'
import { useCreateTransaction } from '../hooks/useCreateTransaction'

export function TransactionRegisterPage() {
  const navigate = useNavigate()
  const { createTransaction, loading, error } = useCreateTransaction()
  const [activeTab, setActiveTab] = useState<TransactionType>('expense')

  /**
   * 폼 제출 핸들러
   */
  const handleSubmit = async (input: TransactionInput) => {
    await createTransaction(input)
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* 헤더 (Sticky) */}
      <header className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 flex items-center justify-between gap-3">
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">거래 등록</h1>
        {/*
          여러 건을 넣을 때는 저장할 때마다 목록을 다시 읽는 왕복이 반복돼 느리다.
          그런 경우를 위한 일괄 입력 화면으로 보내는 진입점.
        */}
        <button
          type="button"
          onClick={() => navigate('/register/bulk')}
          className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          여러 건 입력
        </button>
      </header>

      {/* 수입/지출 탭 (Sticky) */}
      <div className="sticky top-[var(--header-height)] z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2">
        <div className="flex gap-2">
          <button
            onClick={() => setActiveTab('expense')}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-colors ${
              activeTab === 'expense'
                ? 'bg-expense text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            지출
          </button>
          <button
            onClick={() => setActiveTab('income')}
            className={`flex-1 px-4 py-3 rounded-lg font-semibold transition-colors ${
              activeTab === 'income'
                ? 'bg-income text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            수입
          </button>
        </div>
      </div>

      {/* 에러 토스트 */}
      {error && (
        <div className="mx-4 mt-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* 폼 영역 */}
      <div className="px-4 py-6 pb-32">
        <TransactionForm
          key={activeTab} // 탭 변경 시 폼 초기화
          initialType={activeTab}
          onSubmit={handleSubmit}
          loading={loading}
        />
      </div>
    </div>
  )
}
